import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    Chip,
    Divider,
    List,
    ListItem,
    Paper,
    Stack,
    Grid
} from '@mui/material';
import {
    Close as CloseIcon,
    Analytics as AnalyticsIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';

const AnalyzerDrawer = ({ isOpen, onClose, analyzers, onDismiss }) => {
    const [notifications, setNotifications] = useState([]);

    /**
     * Normalize analyzer values so Typography always receives text.
     */
    const formatAnalyzerValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return 'N/A';
        }
        if (Array.isArray(value)) {
            return value.length ? value.join(', ') : 'N/A';
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch (error) {
                return 'N/A';
            }
        }
        return String(value);
    };

    useEffect(() => {
        setNotifications(analyzers);
    }, [analyzers]);

    const handleDismiss = (id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
        if (onDismiss) {
            onDismiss(id);
        }
    };

    const handleCloseDrawer = () => {
        // Don't clear notifications when closing drawer - keep them for when user reopens
        if (onClose) {
            onClose();
        }
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleCloseDrawer}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 600, md: 700 }, // Made drawer bigger
                    bgcolor: 'background.default'
                }
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        borderRadius: 0
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AnalyticsIcon />
                            <Typography variant="h6" component="h2">
                                New Analyzer Data
                            </Typography>
                        </Stack>
                        <IconButton
                            onClick={handleCloseDrawer}
                            sx={{ color: 'inherit' }}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </Paper>

                {/* Content */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                    {notifications.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '50%',
                                color: 'text.secondary'
                            }}
                        >
                            <AnalyticsIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                            <Typography variant="body1">
                                No new analyzer data
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {notifications.map((analyzer, index) => (
                                <ListItem key={analyzer.event_id || analyzer.id || index} sx={{ p: 0, mb: 2 }}>
                                    <Card
                                        elevation={2}
                                        sx={{
                                            width: '100%',
                                            transition: 'box-shadow 0.2s',
                                            '&:hover': {
                                                boxShadow: 4
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                                            {/* Card Header with Time and Close Button */}
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <ScheduleIcon fontSize="small" color="action" />
                                                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                                        {formatAnalyzerValue(analyzer.time)}
                                                    </Typography>
                                                </Stack>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDismiss(analyzer.event_id || analyzer.id || index)}
                                                    sx={{ ml: 1 }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>

                                            {/* Multi-column Content Layout */}
                                            <Grid container spacing={2}>
                                                {/* Column 1 */}
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Stack spacing={1.5}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Flux Name:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {formatAnalyzerValue(analyzer.item)}
                                                            </Typography>
                                                        </Box>

                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Zona:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {formatAnalyzerValue(analyzer.zone)}
                                                            </Typography>
                                                        </Box>

                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Name:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {formatAnalyzerValue(analyzer.analyze_name)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Grid>

                                                {/* Column 2 */}
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <Stack spacing={1.5}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Tag:
                                                            </Typography>
                                                            <Box sx={{ mt: 0.5 }}>
                                                                <Chip
                                                                    label={formatAnalyzerValue(analyzer.tag)}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: analyzer.color || '#e91e63',
                                                                        color: 'white',
                                                                        fontWeight: 'medium'
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>

                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Clasa:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {formatAnalyzerValue(analyzer.text_tags)}
                                                            </Typography>
                                                        </Box>

                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Object ID:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {formatAnalyzerValue(analyzer.object_id)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Grid>

                                                {/* Column 3 */}
                                                <Grid item xs={12} sm={12} md={4}>
                                                    <Stack spacing={1.5}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Value:
                                                            </Typography>
                                                            <Typography
                                                                variant="h6"
                                                                color="primary.main"
                                                                sx={{ mt: 0.5, fontWeight: 'bold' }}
                                                            >
                                                                {formatAnalyzerValue(analyzer.value)}
                                                            </Typography>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                Enrichment:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                {formatAnalyzerValue(analyzer.enrichment_label || analyzer.enrichment || analyzer.enrichment_status)}
                                                            </Typography>
                                                        </Box>
                                                        {analyzer.image_thumb_url && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                    Image:
                                                                </Typography>
                                                                <Box sx={{ mt: 0.5 }}>
                                                                    <a href={analyzer.image_url} target="_blank" rel="noreferrer">
                                                                        <img
                                                                            src={analyzer.image_thumb_url}
                                                                            alt="Analyzer"
                                                                            style={{ width: 120, borderRadius: 6 }}
                                                                        />
                                                                    </a>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                        {!analyzer.image_thumb_url && analyzer.image_pending && (
                                                            <Box>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                                                    Image:
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                    Image pending.
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer' }}
                                                                    onClick={() => window.refreshAnalyzerTable && window.refreshAnalyzerTable()}
                                                                >
                                                                    Retry image
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </Grid>
                                            </Grid>

                                            {/* Divider for visual separation */}
                                            <Divider sx={{ my: 2 }} />

                                            {/* Additional Info Message */}
                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                Analyzer data has been added to the table. If not visible, please check your filters, sorting, and current page.
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default AnalyzerDrawer;
